import { TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";

import ExcelFile from "react-data-export/dist/ExcelPlugin/components/ExcelFile";
import ExcelColumn from "react-data-export/dist/ExcelPlugin/elements/ExcelColumn";
import ExcelSheet from "react-data-export/dist/ExcelPlugin/elements/ExcelSheet";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../App";
import { HTTP_STATUSES, PAYMENT_TYPE, STANDARD_ERRORS_WAY, TRANSACTION_TYPE_PAYMENT } from "../../../utils/consts";
import { fetchFilterData } from "../../../utils/fetchFilterData";
import { parseAxiosError } from "../../../utils/response";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import { StyledCreateBatches, StyledFileUpload } from "../../batches/styledBatches";
import DefaultButton from "../../styles/materialButtonStyle";
import ConnectionsSelect from "../connection/ConnectionsSelect";
import TransactionType from "../invoiceDialog/select/TransactionTypeSelect";
import Notification from "../notification/Notification";
import ProjectsSelect from "../projects/ProjectsSelect";
import ServiceRelationshipsSelect from "../serviceReletionships/ServiceRelationshipsSelect";

import MaterialDialog from "../../styles/materialDialog";
import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../styles/styledDialog";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";

const CreateBatches = ({ fetchBatches }) => {
  const { t } = useTranslation("batchesPage");
  const { user } = useContext(AppContext);

  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const [exampleExcelData, setExampleExcelData] = useState(null);

  const defaultData = {
    "name": null,
    "project": null,
    "service": null,
    "currency": null,
    "paymentSystem": null,
    "connection": null,
    "attributes": null,
    "returnUrl": process.env.REACT_APP_DEFAULT_URL_FOR_INVOICE,
    "callBackUrl": process.env.REACT_APP_DEFAULT_URL_FOR_INVOICE,
    "transactionType": TRANSACTION_TYPE_PAYMENT.ORDER,
    "twofa": null
  };

  const fieldRefs = {
    name: useRef(null),
    project: useRef(null),
    service: useRef(null),
    currency: useRef(null),
    paymentSystem: useRef(null),
    connection: useRef(null),
    returnUrl: useRef(null),
    callBackUrl: useRef(null),
    twofa: useRef(null),
    file: useRef(null),
  };

  const [batchesData, setBatchesData] = useState(defaultData);
  const [batchesFile, setBatchesFile] = useState(null);
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const validateFields = () => {
    const newErrors = {};
    const errorMessage = "This field is required";

    if (!batchesFile) newErrors.file = "File is required";

    Object.keys(fieldRefs).forEach(key => {
      if (key !== "file" && !batchesData[key]) {
        newErrors[key] = errorMessage;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];

      if (fieldRefs[firstErrorField] && fieldRefs[firstErrorField].current) {
        fieldRefs[firstErrorField].current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field) => {
    if (errors[field]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const fetchExampleCsvData = (data) => {
    let csvData = [], countRows = 2, index = 0;

    while (index < countRows) {

      let tmpData = {};

      data.forEach((value, key) => {
        tmpData[0] = "Example Note";
        tmpData[1] = "50";

        tmpData[key + 2] = value.placeholder && value.placeholder !== ""
          ? value.placeholder
          : `Example ${value.title}`;

        csvData.push(tmpData);

        index++;
      });
    }

    setExampleExcelData(csvData);
  };

  const getAttributes = (data) => {
    axios.get("/api/transaction-attributes?currency.asset=" + batchesData.currency.asset + "&paymentSystem.subName=" +
      batchesData.paymentSystem.subName + "&service.name=" + batchesData.service.name + "&transactionType=payout",
      userAuthenticationConfig()).then(response => {

      if (response.data["hydra:member"].length === 0) {
        setNotification({
          ...notification,
          visible: true,
          type: "error",
          message: t("create.errors.getAttrMessage")
        });
        return;
      }

      setBatchesData({
        ...batchesData,
        attributes: response.data["hydra:member"],
        connection: data
      });

      fetchExampleCsvData(response.data["hydra:member"]);

    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const getAvailableBalance = () => {
    let filterUrl = fetchFilterData({
      ["currency"]: batchesData.currency.asset,
      ["service"]: batchesData.service.name,
      ["connection"]: batchesData.connection.id,
      ["paymentSystem"]: batchesData.paymentSystem.subName
    });

    axios.get("/api/balances/converted" + filterUrl, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK && Object.keys(response.data).length) {
        setBalanceInfo(response.data);
      }
    }).catch(error => {
      setNotification({ ...notification, visible: true, type: "error", message: error.response.data.message });
    });
  };

  const parseFile = (event) => {
    if (!event.target?.files || !event.target.files[0]) {
      setBatchesFile(null);
      return;
    }

    setBatchesFile(event.target.files[0]);
  };

  useEffect(() => {
    if (
      !batchesData.currency || !batchesData.service ||
      !batchesData.paymentSystem || !batchesData.project || !batchesData.connection
    ) {
      setBalanceInfo(null);
      return;
    }

    getAvailableBalance();
  }, [batchesData.paymentSystem, batchesData.currency, batchesData.connection]);

  const createBatches = async (event) => {
    event.preventDefault();

    setDisabled(true);

    if (!validateFields()) {
      setDisabled(false);

      return;
    }

    if (!batchesFile) {
      setErrors(prevErrors => ({ ...prevErrors, file: "File is required" }));
      setDisabled(false);

      return;
    }

    let formData = new FormData();

    formData.append("name", batchesData.name);
    formData.append("attributes", JSON.stringify(batchesData.attributes));
    formData.append("currency", batchesData.currency.asset);
    formData.append("paymentSystem", batchesData.paymentSystem.subName);
    formData.append("connection", batchesData.connection.id);
    formData.append("file", batchesFile, batchesFile.name);
    formData.append("callBackUrl", batchesData.callBackUrl);
    formData.append("returnUrl", batchesData.returnUrl);
    formData.append("transactionType", batchesData.transactionType);
    formData.append("paymentType", PAYMENT_TYPE.bath);
    formData.append("twofa", batchesData.twofa);

    axios.post("/api/create-batches", formData, userAuthenticationConfig(false)).then(response => {
      if (response.status === 200) {
        fetchBatches();
        setNotification({
          ...notification,
          visible: true,
          type: "success",
          message: t("create.success")
        });
        handleClose();
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => setDisabled(false));
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setBalanceInfo(null);
    setBatchesData(defaultData);
    setBatchesFile(null);
    setOpen(false);
  };

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledCreateBatches className="batches-head__create-invoice">
        <DefaultButton
          color="success"
          onClick={handleClickOpen}
        >
          {t("create.form.create")}
        </DefaultButton>
        <MaterialDialog
          open={open}
        >
          <StyledDialogWrapper>
            <StyledDialogHead>
              {t("create.form.title")}
            </StyledDialogHead>
            <StyledDialogContent>
              <TextField
                id="name"
                label={t("create.form.name")}
                value={batchesData.name}
                onChange={(data) => {
                  setBatchesData({
                    ...batchesData,
                    name: data.target.value,
                  });
                  clearError("name");
                }}
                error={errors.name}
                helperText={errors.name}
                ref={fieldRefs.name}
              />
              <ProjectsSelect
                checkedProjects={batchesData.project}
                setCheckedProjects={(data) => {
                  setBatchesData({ ...batchesData, project: data });
                  clearError("project");
                }}
                isObject={true}
                error={errors.project}
                refField={fieldRefs.project}
              />
              <ServiceRelationshipsSelect
                project={batchesData.project}
                setCheckedServiceRelationships={(data) => {
                  setBatchesData({
                    ...batchesData,
                    service: data.service,
                    currency: data.currency,
                    paymentSystem: data.paymentSystem,
                    attributes: null,
                    connection: null
                  });
                }}
                balanceRefiling={false}
                errors={errors}
                clearError={clearError}
                fieldRefs={fieldRefs}
              />
              {batchesData.project && batchesData.service && batchesData.paymentSystem &&
                <ConnectionsSelect
                  checkedConnection={batchesData.connection}
                  setCheckedConnection={(data) => {
                    if (batchesData.service && batchesData.currency && batchesData.paymentSystem) {
                      getAttributes(data);
                    }
                    clearError("connection");
                  }}
                  project={batchesData.project.id}
                  service={batchesData.service.name}
                  isObject={true}
                  error={errors.connection}
                  refField={fieldRefs.connection}
                />
              }
              {balanceInfo ?
                <Alert severity={balanceInfo.availableBalance <= 0 ? "warning" : "info"}>
                  <p>
                    {t("create.form.balance")}: {balanceInfo.totalBalance} {balanceInfo?.currency}
                  </p>
                  <p>
                    {t("create.form.holdingBalance")}: {balanceInfo.holdingBalance} {balanceInfo?.currency}
                  </p>
                  <p>
                    {t("create.form.availableBalance")}: {balanceInfo.availableBalance} {balanceInfo?.currency}
                  </p>
                </Alert> :
                <></>
              }
              {batchesData.attributes &&
                <Alert severity={errors.file ? "error" : "info"} ref={fieldRefs.file}>
                  <div className="example-csv">
                    <b>{t("create.form.example")}:</b>
                    <ul>
                      <li style={{ padding: "5px 0" }}>
                        1. Note (Example: Example Note)
                      </li>
                      <li style={{ padding: "5px 0" }}>
                        2. Amount (Example: 50)
                      </li>
                      {batchesData.attributes.map((value, key) => (
                        <li key={key} style={{ padding: "5px 0" }}>
                          {key + 3}. {value.name} (Example:
                          {value.placeholder && value.placeholder !== ""
                            ? ` ${value.placeholder}`
                            : ` Example ${value.title}`
                          })
                        </li>
                      ))}
                    </ul>
                  </div>
                  <StyledFileUpload>
                    {exampleExcelData &&
                      <ExcelFile element={<button type="button">{t("create.form.downloadExample")}</button>}>
                        <ExcelSheet
                          data={exampleExcelData}
                          name="Example"
                        >
                          {Object.keys(exampleExcelData[0]).map((value, key) => (
                            <ExcelColumn
                              name={null}
                              value={key}
                            />
                          ))}
                        </ExcelSheet>
                      </ExcelFile>
                    }
                    <input
                      id="file"
                      type="file"
                      onChange={(event) => {
                        parseFile(event);
                        clearError("file");
                      }}
                      accept=".csv, text/csv, .xlsx"
                    />
                  </StyledFileUpload>
                  {errors.file &&
                    <FormControl error={!!errors.file} ref={fieldRefs.file}>
                      <FormHelperText>{errors.file}</FormHelperText>
                    </FormControl>
                  }
                </Alert>
              }
              <TextField
                id="returnUrl"
                label="returnUrl"
                value={batchesData.returnUrl}
                onChange={(data) => {
                  setBatchesData({
                    ...batchesData,
                    returnUrl: data.target.value
                  });
                  clearError("returnUrl");
                }}
                error={errors.returnUrl}
                helperText={errors.returnUrl}
              />
              <TextField
                id="callBackUrl"
                label="callBackUrl"
                value={batchesData.callBackUrl}
                onChange={(data) => {
                  setBatchesData({
                    ...batchesData,
                    callBackUrl: data.target.value
                  });
                  clearError("callBackUrl");
                }}
                error={errors.callBackUrl}
                helperText={errors.callBackUrl}
              />
              <TransactionType
                checkedTransactionType={batchesData.transactionType}
                setCheckedTransactionType={(data) => setBatchesData({ ...batchesData, "transactionType": data })}
                direction={"payouts"}
              />
              <TextField
                id="twofa"
                label={t("create.form.twofa")}
                value={batchesData.twofa}
                onChange={(data) => {
                  setBatchesData({
                    ...batchesData,
                    twofa: data.target.value
                  });
                  clearError("twofa");
                }}
                error={errors.twofa}
                helperText={errors.twofa}
              />
            </StyledDialogContent>
            <StyledDialogAction col="2">
              <DefaultButton
                onClick={handleClose}
                color="danger"
                disabled={disabled}
              >
                {t("create.form.cancel")}
              </DefaultButton>
              <DefaultButton
                onClick={createBatches}
                color="success"
                disabled={disabled}
              >
                {t("create.form.create")}
              </DefaultButton>
            </StyledDialogAction>
          </StyledDialogWrapper>
        </MaterialDialog>
      </StyledCreateBatches>
    </>
  );
};

export default CreateBatches;